import { useNavigate, useOutletContext, useParams } from "react-router";
import React, { useEffect, FC, useMemo } from "react";
import { CampaignBody } from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../../../store";

import { useRequestHandlers } from "../../../app/hooks/useRequestHandlers";

import { NamedRow } from "../../../components/GeneralUI/NamedRow/NamedRow";
import { Restricted } from "../../../components/Restricted";

import { Methods } from "../../../campaign/details/components/Methods/Methods";

import { capitalizeAssociatedPress } from "../../../helpers/string/capitalize";
import { VPDAdvanced } from "../../../campaign/details/components/VPDAdvanced";
import { AssignedProducts } from "../../../campaign/details/components/modals/CategoryTree/AssignedProducts";
import { DetailedWrapper } from "../../../ui/layout/general/DetailedWrapper/DetailedWrapper";
import { Fields } from "../../../ui/layout/detailedPages/Fields";

import { InterfaceControls } from "../../../campaign/details/components/InterfaceControls/InterfaceControls";
import { styledIcon } from "../../../ui/layout/general/styledIcon";
import { MEDIUM_ICON_SIZE_STYLES } from "../../../ui/styles";
import { NamedSwitch } from "../../../ui/layout/molecules/NamedSwitch";
import { Influencers } from "../../../campaign/details/components/Influencers/Influencers";
import { CampaignBooleanKeys } from "../../../campaign/types";
import {
  campaignTypeTitles,
  generalInformationSwitchableSettings,
  primitiveSettingsTitles,
  selectorModalsTitles,
  switchableSettingsTitles,
} from "../../../campaign/constants";
import { SlideOverFieldNamedRow } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverSingleField/SlideOverFieldNamedRow";
import { PredictableAttributesAcceptableRow } from "../../../campaign/details/components/PredictableAttributesAcceptableRow";
import { handleCampaign } from "../../../store/features/campaigns/handleCampaign";
import { CompanyAndSiteDetails } from "../../types";
import { LinkedBoosting } from "../../../campaign/components/LinkedBoosting";
import { BOOSTING_ELIGIBLE_CAMPAIGN_TYPES } from "../../../campaign/boosting/constants";

export const CampaignDetails: FC<React.PropsWithChildren> = () => {
  const { companyId, siteId, siteDetails } =
    useOutletContext<CompanyAndSiteDetails>();

  const { campaignId } = useParams();

  const campaign = useAppSelector((state) =>
    state.campaigns.campaigns.find((c) => c.id === campaignId),
  );

  const navigate = useNavigate();
  const handlerParams = useRequestHandlers();

  const { handleUpsertCampaignData, handleCampaignDeletion } = handleCampaign(
    handlerParams,
    { companyId, siteId },
  );

  const isVpdCampaign = campaign?.type === "VPD";
  const isVpsCampaign = campaign?.type === "VPS";
  const isTextSearchCampaign = campaign?.type === "TEXT_SEARCH";
  const campaignType = campaign?.type ?? "VPR";

  const backLink = `/company/${companyId}/site/${siteId}/campaigns`;

  useEffect(() => {
    const navigateBack = () => navigate(backLink);
    if (!campaign) navigateBack();
  }, [campaign, backLink, navigate]);

  const handleDeleteCampaign = async () => {
    if (!campaign) return false;
    return await handleCampaignDeletion(campaign);
  };

  const updateCampaignBody = async (
    dataToUpdate: CampaignBody,
    requestType = "campaign",
  ) => {
    if (!campaign) return false;
    const result = await handleUpsertCampaignData(campaign, {
      requestType: requestType,
      dataToUpdate,
    });
    return !!result;
  };

  const renderSwitchableSettings = (key: CampaignBooleanKeys) => {
    if (isVpdCampaign && key === "isDefault") return null;
    return (
      <NamedSwitch
        title={switchableSettingsTitles[key]}
        value={!!campaign?.[key]}
        onChange={(val) => updateCampaignBody({ [key]: val })}
        permissions="campaign:basic_config:update"
        styleVariant="detailPage"
        key={key}
      />
    );
  };

  const widgetProps = useMemo(() => {
    if (isVpsCampaign || isTextSearchCampaign)
      return {
        token: siteDetails.credentials.jwt.readOnly,
        campaignId,
        showTextSearch: isTextSearchCampaign,
        hideImageSearch: isTextSearchCampaign,
      };
  }, [
    isVpsCampaign,
    isTextSearchCampaign,
    siteDetails.credentials.jwt.readOnly,
    campaignId,
  ]);

  return (
    <DetailedWrapper
      deleteAction={handleDeleteCampaign}
      backLink={backLink}
      detailedPageType="detail"
      detailedPage="service"
      widgetProps={widgetProps}
    >
      <Fields title="General settings">
        <SlideOverFieldNamedRow
          onAccept={(value) => updateCampaignBody({ name: value })}
          namedRowTitle={primitiveSettingsTitles.name}
          permissionsGroup="campaign:basic_config"
          slideOverValue={campaign?.name}
        />
        <Restricted permissions="campaign:basic_config:view">
          <NamedRow
            title={capitalizeAssociatedPress(selectorModalsTitles.type)}
            fixedHeight={true}
          >
            <span
              className="inline-flex items-center dark:bg-stone-600 z-10 bg-stone-100 text-stone-900 dark:text-white rounded-md px-2 py-1.5 text-xs"
              key={campaignType}
            >
              <div
                className={`sm:mr-1.5 text-stone-600 dark:text-stone-400 ${MEDIUM_ICON_SIZE_STYLES}`}
              >
                {styledIcon(campaignType)}
              </div>
              <div className="hidden sm:block">
                {campaignTypeTitles[campaignType]}
              </div>
            </span>
          </NamedRow>

          {generalInformationSwitchableSettings.map(renderSwitchableSettings)}

          {isVpdCampaign && (
            <PredictableAttributesAcceptableRow
              updateCampaignBody={updateCampaignBody}
              campaign={campaign}
            />
          )}
        </Restricted>
      </Fields>

      {isVpdCampaign && (
        <Restricted permissions="campaign:advanced_config:view">
          <VPDAdvanced
            campaign={campaign}
            updateCampaignBody={updateCampaignBody}
          />
        </Restricted>
      )}

      {campaign && !isVpdCampaign && (
        <>
          <Methods
            campaign={campaign}
            siteDetails={siteDetails}
            updateCampaignBody={updateCampaignBody}
          />
          <Influencers
            campaign={campaign}
            updateCampaignBody={updateCampaignBody}
          />
          <InterfaceControls
            campaign={campaign}
            updateCampaignBody={updateCampaignBody}
          />
        </>
      )}
      {!isVpdCampaign && campaign && <AssignedProducts campaign={campaign} />}
      {campaign && BOOSTING_ELIGIBLE_CAMPAIGN_TYPES.includes(campaign.type) && (
        <LinkedBoosting
          campaign={campaign}
          updateCampaignBody={updateCampaignBody}
        />
      )}
    </DetailedWrapper>
  );
};
